import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EXPIRY_WARNING_MS,
  SessionEvent,
  extendSession,
  onSessionEvent,
  refreshTokenExpiry,
  rememberReturnPath,
  watchSession,
} from '../lib/session.js';
import { redirectToLogin } from '../lib/auth.js';

// ─────────────────────────────────────────────────────────────────────────────
// Drives the session dialogs from the shell.
//
// Two independent sources feed the same state, and both are needed:
//
//   the SCHEDULE  — the refresh token's own `exp`, so the warning can be raised
//                   BEFORE anything fails. This is what turns an expiry from an
//                   event into a non-event.
//   the EVIDENCE  — a request the BFF actually rejected. Clocks drift, a token
//                   can be revoked early (an admin ends the session, the dentist
//                   signs out in another tab), and in every one of those cases
//                   the schedule still says there is time left.
//
// The schedule alone is what this app was missing entirely: expiry was only
// ever discovered when the next GraphQL call happened, so an odontogram left
// open past the refresh token's end looked fine until the dentist clicked
// something — and then the app navigated away and took the unsaved chart with
// it. The evidence path alone cannot warn anyone; it can only report a loss.
//
// Whichever arrives first wins, and `expired` is terminal: once the session is
// over nothing can walk it back except signing in.
// ─────────────────────────────────────────────────────────────────────────────

export const SessionStatus = {
  ACTIVE: 'active',
  EXPIRING: 'expiring',
  EXPIRED: 'expired',
};

export function useSessionLifecycle() {
  const [status, setStatus] = useState(SessionStatus.ACTIVE);
  const [secondsLeft, setSecondsLeft] = useState(Math.round(EXPIRY_WARNING_MS / 1000));
  const [busy, setBusy] = useState(false);
  const watcherRef = useRef(null);

  useEffect(() => {
    const watcher = watchSession();
    watcherRef.current = watcher;

    const unsubscribe = onSessionEvent((event) => {
      setStatus((prev) => {
        // Terminal. A late-firing `expiring` timer must not downgrade an expiry
        // that a rejected request has already proven — that would put a
        // countdown in front of a dentist whose session is definitively over
        // and offer them an extension that cannot work.
        if (prev === SessionStatus.EXPIRED) return prev;
        if (event === SessionEvent.EXPIRED) return SessionStatus.EXPIRED;
        if (event === SessionEvent.EXPIRING) return SessionStatus.EXPIRING;
        return prev;
      });
    });

    return () => { unsubscribe(); watcher.cancel(); };
  }, []);

  // The countdown ticks only while it is on screen. A once-per-second timer
  // running for a dentist's whole day to service a dialog that is almost never
  // open is pure waste.
  useEffect(() => {
    if (status !== SessionStatus.EXPIRING) return undefined;

    const tick = () => {
      const expiry = refreshTokenExpiry();
      const remaining = expiry ? Math.round((expiry - Date.now()) / 1000) : 0;
      setSecondsLeft(Math.max(0, remaining));
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [status]);

  /** «ادامهٔ کار» — renew silently and put the dialog away. */
  const extend = useCallback(async () => {
    setBusy(true);
    const ok = await extendSession();
    setBusy(false);

    if (!ok) {
      // The refresh token is dead. Retrying would fail identically, and a
      // spinner that never resolves is a worse answer than an honest one.
      setStatus(SessionStatus.EXPIRED);
      return false;
    }

    setStatus(SessionStatus.ACTIVE);
    // Re-arm against the NEW token: a renewal mints a new expiry, and the old
    // timers would otherwise fire against a token that no longer exists.
    watcherRef.current?.rearm();
    return true;
  }, []);

  /**
   * Hide the warning without extending.
   *
   * Does not cancel anything — the schedule still runs and the expired dialog
   * still arrives. A dentist mid-sentence is allowed to wave this away; they
   * are not allowed to switch off the fact that the session is ending.
   */
  const dismissWarning = useCallback(() => {
    setStatus((prev) => (prev === SessionStatus.EXPIRING ? SessionStatus.ACTIVE : prev));
  }, []);

  /** Sign in again, returning to the screen the dentist was interrupted on. */
  const signInAgain = useCallback(async () => {
    setBusy(true);
    rememberReturnPath();
    await redirectToLogin();
  }, []);

  return {
    status,
    secondsLeft,
    busy,
    isExpiring: status === SessionStatus.EXPIRING,
    isExpired: status === SessionStatus.EXPIRED,
    extend,
    dismissWarning,
    signInAgain,
  };
}

export default useSessionLifecycle;
